import { useEffect, useRef, useState } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ArrowDown, ArrowUp, Flame, Minus } from "lucide-react";
import confetti from "canvas-confetti";
import {
  CashCell, ClosePercentCell, SetsCell, ShowPercentCell,
} from "./MetricBreakdownTooltips";

/**
 * Iter128/129 — Sticky "Your Stats" card for the rep's own row on the
 * leaderboard. Iter128 added the card + tooltips. Iter129 adds an
 * at-a-glance delta — `#4 ↑2 vs yesterday` — so reps can tell whether
 * they're trending up or down without scanning the table or toggling
 * Compare mode.
 *
 * Trend data comes from `/api/leaderboard/my-rank-trend?days=1`, which
 * already does the previous-period diff internally (existing endpoint,
 * also used by `MyRankTrendBanner`). We deliberately decouple this fetch
 * from the parent's `compareOn` toggle so the delta is always visible.
 */
const SKIP_ROLES = new Set(["manager", "admin", "owner", "superadmin"]);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MyStatsCard({ currentUser, leaderboard, loading }) {
  const [trend, setTrend] = useState(null);
  const [streak, setStreak] = useState(null);

  // Fetch the rep's personal rank trend whenever they're eligible to see
  // the card. days=1 → "today vs yesterday" — the most timely delta a rep
  // can act on. Re-fetched on focus refreshes via the parent's leaderboard
  // fetch (we key off `leaderboard` length / user_id changing).
  const skipForRole = !currentUser?.user_id || SKIP_ROLES.has((currentUser.role || "").toLowerCase());
  useEffect(() => {
    if (skipForRole) return;
    let aborted = false;
    (async () => {
      try {
        const [trendRes, streakRes] = await Promise.all([
          fetch(`${API}/leaderboard/my-rank-trend?days=1`, { credentials: "include" }),
          fetch(`${API}/leaderboard/my-streak`, { credentials: "include" }),
        ]);
        if (trendRes.ok) {
          const data = await trendRes.json();
          if (!aborted && data?.available) setTrend(data);
        }
        if (streakRes.ok) {
          const data = await streakRes.json();
          if (!aborted) setStreak(data);
        }
      } catch (e) { /* silent — card still renders without trend/streak */ }
    })();
    return () => { aborted = true; };
  }, [skipForRole, currentUser?.user_id, leaderboard.length]);

  // Trigger a confetti burst the moment the rep's deal count flips from 0
  // to >= 1 for the day (Iter131 — "First Sale of the Day" celebration).
  // We persist a per-user/per-day flag in localStorage so the celebration
  // fires exactly once even if the leaderboard refetches multiple times
  // afterwards. Stored as `firstSaleConfettiFired:{user_id}:{YYYY-MM-DD}`.
  const fired = useRef(false);
  useEffect(() => {
    if (skipForRole || !currentUser?.user_id) return;
    const myRow = leaderboard.find((r) => r.user_id === currentUser.user_id);
    if (!myRow) return;
    const dealsToday = Number(myRow.deals || 0);
    if (dealsToday < 1) return;
    const today = new Date().toISOString().slice(0, 10);
    const key = `firstSaleConfettiFired:${currentUser.user_id}:${today}`;
    if (fired.current) return;
    try {
      if (window.localStorage.getItem(key)) {
        fired.current = true;
        return;
      }
    } catch (e) { /* localStorage disabled — celebrate every render is acceptable */ }
    fired.current = true;
    try {
      window.localStorage.setItem(key, "1");
    } catch (e) { /* ignore */ }
    fireFirstSaleConfetti();
  }, [skipForRole, currentUser?.user_id, leaderboard]);

  if (skipForRole) return null;
  if (loading) return null;
  const myRow = leaderboard.find((r) => r.user_id === currentUser.user_id);
  if (!myRow) return null;
  const myIndex = leaderboard.findIndex((r) => r.user_id === currentUser.user_id);
  const rank = myIndex + 1;
  const total = leaderboard.length;
  const rankColor =
    rank === 1
      ? "text-yellow-400"
      : rank <= 3
        ? "text-amber-300"
        : "text-emerald-300";
  return (
    <TooltipProvider delayDuration={150}>
      <div
        className="bg-gradient-to-r from-[#1A1A1A] via-[#1f1f1f] to-[#1A1A1A] border border-emerald-500/30 rounded-xl p-4 sm:p-5 shadow-lg shadow-emerald-500/5"
        data-testid="leaderboard-my-stats-card"
      >
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
          {/* Rank pill */}
          <div className="flex items-center gap-3 lg:border-r lg:border-white/10 lg:pr-6">
            <Flame className="w-5 h-5 text-amber-400 shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Your standing</div>
              <div className="flex items-baseline gap-1.5">
                <span className={`text-2xl sm:text-3xl font-bold ${rankColor} leading-none`}>
                  #{rank}
                </span>
                <span className="text-xs text-gray-500">of {total}</span>
                <RankDelta trend={trend} />
              </div>
              <StreakBadge streak={streak} />
            </div>
          </div>

          {/* KPI strip */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-5 flex-1">
            <Stat
              label="Cash"
              cell={<CashCell rep={myRow} />}
              delta={<CashDelta trend={trend} />}
            />
            <Stat label="Sets" cell={<SetsCell rep={myRow} />} />
            <Stat label="Deals" cell={
              <span className="text-orange-400 font-medium text-base">{myRow.deals || 0}</span>
            } />
            <Stat label="Show %" cell={<ShowPercentCell rep={myRow} />} />
            <Stat label="Close %" cell={<ClosePercentCell rep={myRow} />} />
          </div>
        </div>
        <div className="mt-3 text-[10px] text-gray-500 leading-tight">
          Hover any number to see exactly how it was calculated.
        </div>
      </div>
    </TooltipProvider>
  );
}

/**
 * `↑2 vs yesterday` / `↓1 vs yesterday` / `▬ same as yesterday`. Falls back
 * to a "new" badge when the rep wasn't on the previous board (first day).
 * The label uses "vs yesterday" specifically because we requested days=1;
 * if you switch to days=7 update the copy.
 */
/**
 * Iter132 — Compact streak badge under the rank pill. Renders only when
 * the rep has a streak of 2+ working days (a single day isn't really a
 * "streak"). Color escalates with length so a 7-day streak feels distinct
 * from a 2-day one — purely aesthetic, no functional difference.
 *
 * Copy adapts to `includes_today`:
 *   true  → "🔥 3-day streak" (active, on a roll)
 *   false → "🔥 3-day streak · keep it alive!" (yesterday's still live,
 *                                              prompt to close today)
 */
function StreakBadge({ streak }) {
  if (!streak) return null;
  const days = Number(streak.streak || 0);
  if (days < 2) return null;
  // Tier the styling so visual feedback scales with effort.
  let tone = "bg-amber-500/15 text-amber-300 border-amber-500/30";
  if (days >= 14) {
    tone = "bg-rose-500/20 text-rose-200 border-rose-500/50";
  } else if (days >= 7) {
    tone = "bg-orange-500/20 text-orange-300 border-orange-500/50";
  } else if (days >= 4) {
    tone = "bg-amber-500/20 text-amber-200 border-amber-500/40";
  }
  return (
    <div
      className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${tone}`}
      data-testid="leaderboard-my-stats-streak"
      title={
        streak.includes_today
          ? `On a ${days}-day close streak — last close ${streak.last_close_date}`
          : `${days}-day streak from yesterday — close one today to keep it alive`
      }
    >
      🔥 {days}-day streak
      {/* Iter183 — Show a subtle "N preserved" pill inside the streak
          badge when the rep's current streak includes a bridge from a
          recent break + recovery. Gives visible credit for the
          rebound. */}
      {streak.preserved_from_break && streak.bridged_from_streak && (
        <span
          className="ml-1 text-[9px] font-semibold text-orange-200 bg-orange-500/25 border border-orange-500/40 px-1 rounded-full"
          title={`${streak.bridged_from_streak} days preserved from your pre-break streak — you recovered within 14 days`}
          data-testid="leaderboard-my-stats-streak-preserved"
        >
          {streak.bridged_from_streak} preserved
        </span>
      )}
      {!streak.includes_today && (
        <span className="text-[10px] text-gray-400 font-normal">· keep it alive!</span>
      )}
    </div>
  );
}

function RankDelta({ trend }) {
  if (!trend) return null;
  // Brand-new rep (no prev_rank) — celebrate them with "new!" instead of a stale 0.
  if (trend.prev_rank === null || trend.prev_rank === undefined) {
    return (
      <span
        className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
        data-testid="leaderboard-my-stats-rank-new"
      >
        new
      </span>
    );
  }
  const change = trend.rank_change;
  if (change > 0) {
    return (
      <span
        className="ml-1 inline-flex items-center gap-0.5 text-xs font-semibold text-emerald-400"
        data-testid="leaderboard-my-stats-rank-up"
        title={`Moved up ${change} from #${trend.prev_rank} yesterday`}
      >
        <ArrowUp className="w-3 h-3" />
        {change} vs yesterday
      </span>
    );
  }
  if (change < 0) {
    return (
      <span
        className="ml-1 inline-flex items-center gap-0.5 text-xs font-semibold text-red-400"
        data-testid="leaderboard-my-stats-rank-down"
        title={`Dropped ${Math.abs(change)} from #${trend.prev_rank} yesterday`}
      >
        <ArrowDown className="w-3 h-3" />
        {Math.abs(change)} vs yesterday
      </span>
    );
  }
  return (
    <span
      className="ml-1 inline-flex items-center gap-0.5 text-xs text-gray-500"
      data-testid="leaderboard-my-stats-rank-same"
      title="Same rank as yesterday"
    >
      <Minus className="w-3 h-3" />
      vs yesterday
    </span>
  );
}

/**
 * Iter131 — Twin confetti bursts angled inward from the bottom corners,
 * tinted celebration coral + emerald so it visually pairs with the
 * "First Sale of the Day" Discord embed. Decoupled from the existing
 * `fireConfettiBurst` in Leaderboard.jsx (which fires on a new #1) so
 * the two events can coexist without colliding visually.
 */
function fireFirstSaleConfetti() {
  const defaults = {
    spread: 80,
    startVelocity: 50,
    decay: 0.91,
    ticks: 220,
    gravity: 0.95,
    scalar: 1,
    colors: ["#FF7F00", "#FFD700", "#10b981", "#ffffff", "#34d399"],
    zIndex: 9999,
  };
  confetti({ ...defaults, particleCount: 90, angle: 65, origin: { x: 0.1, y: 0.9 } });
  confetti({ ...defaults, particleCount: 90, angle: 115, origin: { x: 0.9, y: 0.9 } });
}

function Stat({ label, cell, delta = null }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">{label}</div>
      <div className="text-base">{cell}</div>
      {delta}
    </div>
  );
}

/**
 * "+$3,200 vs yesterday" / "−$500 vs yesterday" / "▬ vs yesterday".
 *
 * We read absolute current_cash / prev_cash off the trend payload because
 * `cash_change_pct` flips to 100% on the first-ever sale (prev=0) which
 * isn't a meaningful percentage to surface — but the *dollar* delta still
 * is. Hidden entirely when both totals are 0 (avoids "$0 vs yesterday"
 * noise on a brand-new account).
 */
function CashDelta({ trend }) {
  if (!trend?.available) return null;
  const current = Number(trend.current_cash || 0);
  const prev = Number(trend.prev_cash || 0);
  if (current === 0 && prev === 0) return null;
  const diff = current - prev;
  const fmt = (v) => `$${Math.abs(Math.round(v)).toLocaleString()}`;
  if (diff > 0) {
    return (
      <div
        className="mt-1 inline-flex items-center gap-0.5 text-[11px] font-semibold text-emerald-400"
        data-testid="leaderboard-my-stats-cash-up"
        title={`Up ${fmt(diff)} from ${fmt(prev)} yesterday`}
      >
        <ArrowUp className="w-3 h-3" />
        +{fmt(diff)} vs yesterday
      </div>
    );
  }
  if (diff < 0) {
    return (
      <div
        className="mt-1 inline-flex items-center gap-0.5 text-[11px] font-semibold text-red-400"
        data-testid="leaderboard-my-stats-cash-down"
        title={`Down ${fmt(diff)} from ${fmt(prev)} yesterday`}
      >
        <ArrowDown className="w-3 h-3" />
        −{fmt(diff)} vs yesterday
      </div>
    );
  }
  return (
    <div
      className="mt-1 inline-flex items-center gap-0.5 text-[11px] text-gray-500"
      data-testid="leaderboard-my-stats-cash-same"
      title="Same cash as yesterday"
    >
      <Minus className="w-3 h-3" />
      vs yesterday
    </div>
  );
}
